import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, Building2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Footer } from "@/components/Footer";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    company: "",
  });

  // Password validation requirements
  const passwordRequirements = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
    };
  }, [formData.password]);

  const isPasswordValid = useMemo(() => {
    return Object.values(passwordRequirements).every(Boolean);
  }, [passwordRequirements]);

  const isFormValid = useMemo(() => {
    return formData.name.trim() && formData.email.trim() && isPasswordValid;
  }, [formData.name, formData.email, isPasswordValid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!isPasswordValid) {
      toast.error("Please ensure your password meets all requirements");
      return;
    }

    setLoading(true);
    try {
      // Normalize email to lowercase before sending
      await register(formData.email.toLowerCase().trim(), formData.password, formData.name.trim(), formData.company?.trim());
      toast.success("Account created successfully!");
      // New users go to homepage to browse experts
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col">
      <div className="flex-1 flex">
        {/* Left Side - Branding */}
        <div className="hidden lg:block lg:flex-1 relative bg-gradient-to-br from-charcoal to-obsidian">
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <div className="max-w-md text-center">
              <div className="w-32 h-32 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="font-display text-5xl text-gold-500">EB</span>
              </div>
              <h2 className="font-display text-4xl text-white mb-6">
                Join the Expert Economy
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed">
                Connect with world-class advisors or share your expertise with ambitious professionals worldwide.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <motion.div 
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link to="/" className="inline-block mb-12">
              <h1 className="font-display text-3xl text-gold-gradient">ExpertBase</h1>
            </Link>

            <h2 className="font-display text-4xl mb-2">Create account</h2>
            <p className="text-slate-400 mb-8">Start your journey with ExpertBase</p>

            <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="pl-12 bg-charcoal border-slate-800 focus:border-gold-500 h-12"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="pl-12 bg-charcoal border-slate-800 focus:border-gold-500 h-12"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company <span className="text-slate-500 text-sm">(optional)</span></Label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="company"
                  type="text"
                  placeholder="Your company name"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  className="pl-12 bg-charcoal border-slate-800 focus:border-gold-500 h-12"
                  data-testid="register-company"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="pl-12 pr-12 bg-charcoal border-slate-800 focus:border-gold-500 h-12"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Password Requirements */}
              <div className="mt-2 space-y-1 text-sm">
                <p className="text-slate-400 mb-1">Password must have:</p>
                <div className="grid grid-cols-2 gap-1">
                  <div className={`flex items-center gap-1.5 ${passwordRequirements.minLength ? 'text-green-500' : 'text-slate-500'}`}>
                    {passwordRequirements.minLength ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    <span>8+ characters</span>
                  </div>
                  <div className={`flex items-center gap-1.5 ${passwordRequirements.hasUppercase ? 'text-green-500' : 'text-slate-500'}`}>
                    {passwordRequirements.hasUppercase ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    <span>1 uppercase</span>
                  </div>
                  <div className={`flex items-center gap-1.5 ${passwordRequirements.hasLowercase ? 'text-green-500' : 'text-slate-500'}`}>
                    {passwordRequirements.hasLowercase ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    <span>1 lowercase</span>
                  </div>
                  <div className={`flex items-center gap-1.5 ${passwordRequirements.hasNumber ? 'text-green-500' : 'text-slate-500'}`}>
                    {passwordRequirements.hasNumber ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    <span>1 number</span>
                  </div>
                </div>
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading || !isFormValid}
              className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-obsidian text-slate-500">or continue with</span>
            </div>
          </div>

          {/* Google Sign In Button */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const redirectUrl = window.location.origin + '/dashboard';
              console.log("Google Sign-Up: Redirect URL:", redirectUrl);
              console.log("Google Sign-Up: Full auth URL:", `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white hover:bg-gray-100 text-gray-900 font-semibold py-6 rounded-sm border-0 flex items-center justify-center gap-3"
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>

          <p className="text-center text-slate-400 mt-8">
            Already have an account?{" "}
            <Link to="/login" className="text-gold-500 hover:text-gold-400 transition-colors">
              Sign in
            </Link>
          </p>

          <div className="mt-8 pt-8 border-t border-slate-800">
            <p className="text-center text-slate-500 text-sm">
              Want to share your expertise?{" "}
              <Link to="/become-expert" className="text-gold-500 hover:text-gold-400 transition-colors">
                Become an Expert
              </Link>
            </p>
          </div>
        </motion.div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
